/**
 * Color utility functions for generating accessible color variants
 * Ensures WCAG 2.1 AA compliance (4.5:1 contrast ratio for normal text)
 */

/**
 * Parses a color string (hex, rgb, rgba) and returns RGB values
 */
export const parseColor = (color: string): { r: number; g: number; b: number } | null => {
  // Handle hex colors
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const fullHex =
      hex.length === 3
        ? hex
            .split('')
            .map((c) => c + c)
            .join('')
        : hex;

    if (fullHex.length !== 6) return null;

    return {
      r: parseInt(fullHex.slice(0, 2), 16),
      g: parseInt(fullHex.slice(2, 4), 16),
      b: parseInt(fullHex.slice(4, 6), 16)
    };
  }

  // Handle rgb/rgba colors
  const rgbMatch = color.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1], 10),
      g: parseInt(rgbMatch[2], 10),
      b: parseInt(rgbMatch[3], 10)
    };
  }

  return null;
};

/**
 * Converts RGB to hex color string
 */
export const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Calculates relative luminance of a color (WCAG formula)
 * @see https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
export const getRelativeLuminance = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const sRGB = c / 255;
    return sRGB <= 0.03928 ? sRGB / 12.92 : ((sRGB + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Calculates contrast ratio between two colors
 * @see https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio
 */
export const getContrastRatio = (
  color1: { r: number; g: number; b: number },
  color2: { r: number; g: number; b: number }
): number => {
  const l1 = getRelativeLuminance(color1.r, color1.g, color1.b);
  const l2 = getRelativeLuminance(color2.r, color2.g, color2.b);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Darkens a color by a percentage
 */
export const darkenColor = (
  color: { r: number; g: number; b: number },
  percent: number
): { r: number; g: number; b: number } => {
  const factor = 1 - percent / 100;
  return {
    r: Math.round(color.r * factor),
    g: Math.round(color.g * factor),
    b: Math.round(color.b * factor)
  };
};

/**
 * Lightens a color by a percentage
 */
export const lightenColor = (
  color: { r: number; g: number; b: number },
  percent: number
): { r: number; g: number; b: number } => {
  const factor = percent / 100;
  return {
    r: Math.round(color.r + (255 - color.r) * factor),
    g: Math.round(color.g + (255 - color.g) * factor),
    b: Math.round(color.b + (255 - color.b) * factor)
  };
};

const WHITE = { r: 255, g: 255, b: 255 };
const BLACK = { r: 0, g: 0, b: 0 };

/**
 * WCAG AA minimum contrast ratios
 */
export const WCAG_AA_NORMAL_TEXT = 4.5;
export const WCAG_AA_LARGE_TEXT = 3;
export const WCAG_AA_UI_COMPONENTS = 3;

/**
 * Finds an accessible shade of a color that meets the specified contrast ratio with white text.
 * Progressively darkens the color until it meets the contrast requirement.
 *
 * @param color - The input color (hex, rgb, or rgba string)
 * @param minContrastRatio - Minimum contrast ratio required (default: 4.5 for WCAG AA normal text)
 * @returns An accessible hex color string, or a fallback if the color cannot be parsed
 */
export const getAccessibleColor = (
  color: string,
  minContrastRatio: number = WCAG_AA_NORMAL_TEXT
): string => {
  const parsed = parseColor(color);

  if (!parsed) {
    // Fallback to a safe default if color cannot be parsed
    return '#1976D2';
  }

  // Check if the original color already meets the contrast requirement
  const originalContrast = getContrastRatio(parsed, WHITE);
  if (originalContrast >= minContrastRatio) {
    return rgbToHex(parsed.r, parsed.g, parsed.b);
  }

  // Progressively darken the color until it meets the contrast requirement
  let darkenedColor: { r: number; g: number; b: number };
  const step = 5;
  let totalDarkening = 0;

  while (totalDarkening < 90) {
    totalDarkening += step;
    darkenedColor = darkenColor(parsed, totalDarkening);
    const contrast = getContrastRatio(darkenedColor, WHITE);

    if (contrast >= minContrastRatio) {
      return rgbToHex(darkenedColor.r, darkenedColor.g, darkenedColor.b);
    }
  }

  // If we can't achieve the contrast ratio, return black
  return '#000000';
};

/**
 * Finds an accessible shade of a color for use with black text.
 * Progressively lightens the color until it meets the contrast requirement.
 *
 * @param color - The input color (hex, rgb, or rgba string)
 * @param minContrastRatio - Minimum contrast ratio required (default: 4.5 for WCAG AA normal text)
 * @returns An accessible hex color string
 */
export const getAccessibleColorForBlackText = (
  color: string,
  minContrastRatio: number = WCAG_AA_NORMAL_TEXT
): string => {
  const parsed = parseColor(color);

  if (!parsed) {
    return '#E3F2FD';
  }

  const originalContrast = getContrastRatio(parsed, BLACK);
  if (originalContrast >= minContrastRatio) {
    return rgbToHex(parsed.r, parsed.g, parsed.b);
  }

  let lightenedColor = { ...parsed };
  const step = 5;
  let totalLightening = 0;

  while (totalLightening < 90) {
    totalLightening += step;
    lightenedColor = lightenColor(parsed, totalLightening);
    const contrast = getContrastRatio(lightenedColor, BLACK);

    if (contrast >= minContrastRatio) {
      return rgbToHex(lightenedColor.r, lightenedColor.g, lightenedColor.b);
    }
  }

  return '#FFFFFF';
};

/**
 * Generates a color palette from a base color with accessible variants
 *
 * @param color - The input color (hex, rgb, or rgba string)
 * @returns An object with various accessible shades
 */
export interface IColorPalette {
  original: string;
  accessibleForWhiteText: string;
  accessibleForBlackText: string;
  hover: string;
  light: string;
  dark: string;
}

export const generateColorPalette = (color: string): IColorPalette => {
  const parsed = parseColor(color);

  if (!parsed) {
    return {
      original: '#1976D2',
      accessibleForWhiteText: '#1976D2',
      accessibleForBlackText: '#E3F2FD',
      hover: '#1565C0',
      light: '#BBDEFB',
      dark: '#0D47A1'
    };
  }

  const accessibleForWhite = getAccessibleColor(color);
  const accessibleParsed = parseColor(accessibleForWhite);
  const hoverColor = accessibleParsed
    ? rgbToHex(
        ...(Object.values(darkenColor(accessibleParsed, 10)) as [number, number, number])
      )
    : '#1565C0';

  return {
    original: rgbToHex(parsed.r, parsed.g, parsed.b),
    accessibleForWhiteText: accessibleForWhite,
    accessibleForBlackText: getAccessibleColorForBlackText(color),
    hover: hoverColor,
    light: rgbToHex(
      ...(Object.values(lightenColor(parsed, 70)) as [number, number, number])
    ),
    dark: rgbToHex(
      ...(Object.values(darkenColor(parsed, 30)) as [number, number, number])
    )
  };
};
